"use client";
import Image from "next/image";
import CTALink from "@/components/CTA/ctaLink";
import LocalizedOfferEndsPrefix from "@/components/PromoBanner/components/LocalizedOfferEndsPrefix";
import PromoBannerTooltip from "@/components/PromoBanner/components/PromoBannerTooltip";
import type { SlimV2PromoBannerProps } from "@/components/PromoBanner/types";
import boldTextWithAsterisk from "@/utils/functions/boldTextWithAsterisk";

/**
 * Slim v2 — Side Rail variant
 *
 * Same monarch payload as Slim v2 (`data.slimV2`); used when the page is
 * rendered with a side-rail column:
 *   - Program "redesign" template with `showSideRail`.
 *   - Course "heroSideRail" template with `showSideRail`.
 *
 * Visual differences vs the standard Slim v2:
 *   - Image is on the LEFT (when present) instead of the right.
 *   - Tighter sizing (smaller padding, title and image) so the banner
 *     fits the narrower main-content column (≈824px at xl).
 *   - The end-date row is pinned to the right of the CTA via
 *     `justify-between`.
 *   - When no image is provided, content takes the full container width.
 *
 * Layout breakpoint
 * -----------------
 * Switches from the mobile (stacked, centered) layout to the desktop
 * (image-left, text-right) layout at `slim2-tablet` (906px) — the same
 * custom breakpoint used by the standard Slim v2 (`Slim.v2.tsx`). This
 * keeps both Slim v2 layouts pivoting at the same viewport width.
 */
export default function PromoBannerSlimV2SideRail({
  backgroundImage,
  backgroundType,
  className,
  cta,
  ctaElement,
  description,
  discountCode,
  endDate,
  endDatePrefix,
  image,
  learnMoreLink,
  title,
  tooltip,
}: SlimV2PromoBannerProps) {
  const withBackgroundTreatment =
    className?.includes("promo-banner-with-bg") ?? false;
  const withBackgroundImage =
    backgroundType === "image" && !!backgroundImage?.url;
  const withSolidBackgroundTreatment =
    backgroundType === "solid" || withBackgroundTreatment;
  const hasImage = !!image?.url;

  return (
    <div
      className={`relative overflow-hidden bg-primary rounded-xl p-4 slim2-tablet:p-8 ${className || ""}`}
    >
      {withBackgroundImage && backgroundImage && (
        <Image
          src={backgroundImage.url}
          alt=""
          fill
          className="absolute inset-0 object-cover"
        />
      )}
      {withSolidBackgroundTreatment && (
        <div className="absolute inset-0 bg-black opacity-10" />
      )}

      <div className="relative z-10 flex flex-col items-center gap-4 slim2-tablet:flex-row slim2-tablet:items-center slim2-tablet:gap-6">
        {hasImage && image && (
          <div className="relative shrink-0 overflow-hidden rounded-xl h-[179px] sm:h-[300px] w-full slim2-tablet:h-[256px] slim2-tablet:w-[253px]">
            <Image
              src={image.url}
              alt={image.altText || ""}
              className="object-cover object-top slim2-tablet:object-center"
              fill
            />
          </div>
        )}

        <div className="flex flex-1 flex-col items-center gap-3 min-w-0 text-center slim2-tablet:items-start slim2-tablet:gap-4 slim2-tablet:text-left">
          <h2 className="text-primary-foreground italic m-0 font-black leading-[1.2] text-[28px] slim2-tablet:text-[36px]">
            {title}
          </h2>
          <p className="text-primary-foreground leading-[1.4] text-[14px] slim2-tablet:text-[20px] mb-0">
            {typeof description === "string"
              ? boldTextWithAsterisk(description)
              : description}
          </p>

          <div className="flex flex-col gap-2 w-full slim2-tablet:flex-row slim2-tablet:items-center slim2-tablet:justify-between slim2-tablet:gap-4">
            {cta && !ctaElement && (
              <CTALink
                type="brand"
                size="xl"
                className="py-0 h-10 slim2-tablet:h-[42px] lg:h-[42px] xl:h-[42px] w-full slim2-tablet:w-auto whitespace-nowrap"
                label={cta.label}
                url={cta.url}
                location={cta.location}
                html_id={cta.html_id}
              />
            )}
            {ctaElement && (
              <div className="flex items-center whitespace-nowrap h-10 slim2-tablet:h-[42px] lg:h-[42px] xl:h-[42px] w-full slim2-tablet:w-auto">
                {ctaElement}
              </div>
            )}
            <div className="flex items-center gap-1 justify-center slim2-tablet:justify-end">
              <p className="text-primary-foreground text-xs mb-0">
                <LocalizedOfferEndsPrefix
                  prefix={endDatePrefix}
                  date={endDate}
                />
                .
              </p>
              <PromoBannerTooltip
                discountCode={discountCode}
                endDate={endDate}
                learnMoreLink={learnMoreLink}
                htmlContent={tooltip?.infoText ?? ""}
                size={12}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
